import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, takeUntil, catchError, of } from 'rxjs';
import { IdeasApiService } from '../../shared/api/ideas-api.service';
import { IdeaListItemDto } from '../../shared/api/ideas-api.types';
import { NotificationPreferencesApiService } from '../../shared/api/notification-preferences-api.service';
import { LoaderComponent } from '../../shared/ui/loader/loader.component';

@Component({
  selector: 'app-following',
  standalone: true,
  imports: [CommonModule, RouterLink, LoaderComponent],
  templateUrl: './following.component.html',
  styleUrl: './following.component.css'
})
export class FollowingComponent implements OnInit, OnDestroy {
  ideas: IdeaListItemDto[] = [];
  loading = false;
  errorMessage = '';
  page = 1;
  pageSize = 20;
  totalCount = 0;
  savingUnfollowId: string | null = null;
  savingPrefs = false;

  emailNotifications = true;
  ideaStatusChanges = true;
  newComments = true;
  followedIdeaUpdates = true;
  challengeAnnouncements = true;
  weeklyDigest = false;
  /** Notification preferences panel expanded (collapsed by default to emphasize the followed list). */
  prefsExpanded = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly api: IdeasApiService,
    private readonly prefsApi: NotificationPreferencesApiService
  ) {}

  ngOnInit(): void {
    this.loadPrefs();
    this.loadFollowedIdeas();
  }

  private loadPrefs(): void {
    this.prefsApi
      .get()
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe({
        next: (prefs) => {
          if (prefs) {
            this.emailNotifications = prefs.emailNotifications;
            this.ideaStatusChanges = prefs.ideaStatusChanges;
            this.newComments = prefs.newComments;
            this.followedIdeaUpdates = prefs.followedIdeaUpdates;
            this.challengeAnnouncements = prefs.challengeAnnouncements;
            this.weeklyDigest = prefs.weeklyDigest;
          }
        }
      });
  }

  private updatePrefs(): void {
    if (this.savingPrefs) return;
    this.savingPrefs = true;
    this.prefsApi
      .update({
        emailNotifications: this.emailNotifications,
        ideaStatusChanges: this.ideaStatusChanges,
        newComments: this.newComments,
        followedIdeaUpdates: this.followedIdeaUpdates,
        challengeAnnouncements: this.challengeAnnouncements,
        weeklyDigest: this.weeklyDigest
      })
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe({
        next: (prefs) => {
          if (prefs) {
            this.emailNotifications = prefs.emailNotifications;
            this.ideaStatusChanges = prefs.ideaStatusChanges;
            this.newComments = prefs.newComments;
            this.followedIdeaUpdates = prefs.followedIdeaUpdates;
            this.challengeAnnouncements = prefs.challengeAnnouncements;
            this.weeklyDigest = prefs.weeklyDigest;
          }
          this.savingPrefs = false;
        },
        error: () => {
          this.savingPrefs = false;
        }
      });
  }

  toggleEmailNotifications(): void {
    this.emailNotifications = !this.emailNotifications;
    this.updatePrefs();
  }

  toggleNewComments(): void {
    this.newComments = !this.newComments;
    this.updatePrefs();
  }

  toggleIdeaStatusChanges(): void {
    this.ideaStatusChanges = !this.ideaStatusChanges;
    this.updatePrefs();
  }

  toggleFollowedIdeaUpdates(): void {
    this.followedIdeaUpdates = !this.followedIdeaUpdates;
    this.updatePrefs();
  }

  toggleChallengeAnnouncements(): void {
    this.challengeAnnouncements = !this.challengeAnnouncements;
    this.updatePrefs();
  }

  toggleWeeklyDigest(): void {
    this.weeklyDigest = !this.weeklyDigest;
    this.updatePrefs();
  }

  togglePrefsSection(): void {
    this.prefsExpanded = !this.prefsExpanded;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFollowedIdeas(): void {
    this.loading = true;
    this.errorMessage = '';
    this.api
      .getFollowedIdeas(this.page, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.ideas = result.items ?? [];
          this.totalCount = result.totalCount ?? 0;
          this.loading = false;
        },
        error: () => {
          this.errorMessage = 'Failed to load followed ideas.';
          this.loading = false;
        }
      });
  }

  unfollow(idea: IdeaListItemDto): void {
    if (this.savingUnfollowId) return;
    this.savingUnfollowId = idea.id;
    this.api
      .unfollowIdea(idea.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.ideas = this.ideas.filter((i) => i.id !== idea.id);
          this.totalCount = Math.max(0, this.totalCount - 1);
          this.savingUnfollowId = null;
        },
        error: () => {
          this.savingUnfollowId = null;
        }
      });
  }

  getStatusLabel(status: number): string {
    const labels: Record<number, string> = {
      0: 'Draft',
      1: 'Submitted',
      2: 'Triage',
      3: 'Under Review',
      4: 'Accepted',
      5: 'Rejected',
      6: 'Planned',
      7: 'In Progress',
      8: 'Delivered',
      9: 'Archived',
      10: 'Need more info'
    };
    return labels[status] ?? 'Unknown';
  }

  getStatusClass(status: number): string {
    const classes: Record<number, string> = {
      0: 'tag-draft',
      1: 'tag-submitted',
      2: 'tag-triage',
      3: 'tag-under-review',
      4: 'tag-approved',
      5: 'tag-rejected',
      6: 'tag-planned',
      7: 'tag-in-progress',
      8: 'tag-delivered',
      9: 'tag-archived',
      10: 'tag-need-info'
    };
    return classes[status] ?? 'tag-default';
  }

  formatDate(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffSec < 60) return 'less than a minute ago';
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `about ${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `about ${Math.floor(diffDays / 30)} months ago`;
    return `about ${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
  }
}
